// src/pages/sessions/components/LiveSession/VideoGrid.jsx
import React, { useEffect, useState, useMemo, memo, useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrackRefContext,
  useRoomContext,
  useTracks,
  ParticipantTile,
} from '@livekit/components-react';
import { Track, ConnectionQuality } from 'livekit-client';
import { ComputerDesktopIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, VideoCameraIcon, SpeakerXMarkIcon, SignalSlashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon, ArrowsPointingOutIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import { getDeviceType } from '@/utils/platform';
import { toast } from 'sonner';
import '@livekit/components-styles';

// Store room names globally with cleanup
const globalRoomNamesRef = { current: new Map() };

// Enhanced cleanup utility class
class VideoGridCleanup {
  constructor() {
    this.listeners = new Map();
    this.timeouts = new Set();
    this.disposed = false;
  }

  setTimeout(fn, delay) {
    if (this.disposed) return null;
    const id = setTimeout(() => {
      this.timeouts.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timeouts.add(id);
    return id;
  }

  clearTimeout(id) {
    if (id) {
      clearTimeout(id);
      this.timeouts.delete(id);
    }
  }

  addListener(participant, event, handler) {
    if (this.disposed || !participant) return null;
    
    const key = `${participant.sid || participant.identity}-${event}-${Date.now()}`;
    
    this.removeListener(key);
    
    try {
      participant.on(event, handler);
      this.listeners.set(key, { participant, event, handler });
      return key;
    } catch (e) {
      return null;
    }
  }

  removeListener(key) {
    const listener = this.listeners.get(key);
    if (listener) {
      try {
        listener.participant.off(listener.event, listener.handler);
      } catch (e) {
        // Silent cleanup
      }
      this.listeners.delete(key);
    }
  }

  clearAllListeners() {
    this.listeners.forEach((listener, key) => {
      try {
        listener.participant.off(listener.event, listener.handler);
      } catch (e) {
        // Silent cleanup
      }
    });
    this.listeners.clear();
  }

  cleanup() {
    this.disposed = true;
    
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts.clear();
    
    this.clearAllListeners();
  }
}

// Calculate optimal grid layout based on container size and participant count
const calculateOptimalGrid = (containerWidth, containerHeight, participantCount) => {
  if (participantCount === 0) return { cols: 1, rows: 1 };
  if (participantCount === 1) return { cols: 1, rows: 1 };
  
  if (participantCount === 2) {
    const containerAspect = containerWidth / containerHeight;
    if (containerAspect > 1.2) {
      return { cols: 2, rows: 1 };
    }
    return { cols: 1, rows: 2 };
  }
  
  const targetVideoAspect = 16 / 9;
  let bestLayout = { cols: 1, rows: participantCount };
  let bestScore = -Infinity;
  
  for (let cols = 1; cols <= participantCount; cols++) {
    const rows = Math.ceil(participantCount / cols);
    const videoWidth = (containerWidth - (cols + 1) * 8) / cols;
    const videoHeight = (containerHeight - (rows + 1) * 8) / rows;
    const videoAspect = videoWidth / videoHeight;
    
    if (videoAspect < 0.75) continue;
    
    let actualWidth, actualHeight;
    if (videoAspect > targetVideoAspect) {
      actualHeight = videoHeight;
      actualWidth = videoHeight * targetVideoAspect;
    } else {
      actualWidth = videoWidth;
      actualHeight = videoWidth / targetVideoAspect;
    }
    
    const videoArea = actualWidth * actualHeight;
    const utilization = (videoArea * participantCount) / (containerWidth * containerHeight);
    const aspectDiff = Math.abs(videoAspect - targetVideoAspect);
    
    let aspectPenalty = aspectDiff * 0.1;
    if (videoAspect < 1.0) {
      aspectPenalty = aspectDiff * 0.3;
    }
    
    const score = videoArea * utilization * (1 - aspectPenalty);
    
    if (score > bestScore) {
      bestScore = score;
      bestLayout = { cols, rows };
    }
  }
  
  return bestLayout;
};

// Helper to get participant name
const getParticipantName = (participant, localParticipantIdentity, t) => {
  if (!participant) return t('videoGrid.someone');
  
  const isMe = participant.identity === localParticipantIdentity;
  if (isMe) return t('videoGrid.you');
  
  let metadata = {};
  try {
    if (participant.metadata) metadata = JSON.parse(participant.metadata);
  } catch (e) {}
  
  return metadata.displayName || participant.name || t('videoGrid.someone');
};

// Smart Banner Component
const ScreenShareBanner = memo(({ 
  screenShares, 
  activeIndex, 
  onSelectShare,
  localParticipantIdentity 
}) => {
  const { t } = useTranslation();

  if (screenShares.length === 0) return null;
  
  const currentSharer = screenShares[activeIndex];
  const currentName = getParticipantName(currentSharer?.participant, localParticipantIdentity, t);
  
  return (
    <div className="screen-share-banner">
      <ComputerDesktopIcon className="w-5 h-5 flex-shrink-0" />
      {screenShares.length === 1 ? (
        <span>{t('videoGrid.screenShare.sharing', { name: currentName })}</span>
      ) : (
        <div className="flex items-center gap-3">
          <span>{t('videoGrid.screenShare.multipleSharing', { count: screenShares.length })}</span>
          <div className="flex items-center gap-2 flex-wrap">
            {screenShares.map((share, index) => {
              const name = getParticipantName(share.participant, localParticipantIdentity, t);
              const isActive = index === activeIndex;
              return (
                <button
                  key={share.publication.trackSid}
                  onClick={() => onSelectShare(index)}
                  className={`px-2 py-1 rounded text-sm transition-colors ${
                    isActive 
                      ? 'bg-cyan-600 text-white font-semibold' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.screenShares.length === nextProps.screenShares.length &&
         prevProps.activeIndex === nextProps.activeIndex &&
         prevProps.localParticipantIdentity === nextProps.localParticipantIdentity;
});

ScreenShareBanner.displayName = 'ScreenShareBanner';

// Zoomable Screen Share Component
const ZoomableScreenShare = memo(({ trackRef }) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showControls, setShowControls] = useState(false);
  
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const videoRef = useRef(null);
  const hideControlsTimeoutRef = useRef(null);
  const attachedTrackRef = useRef(null);

  // Touch tracking refs for pinch-to-zoom and drag
  const lastTouchDistRef = useRef(null);
  const lastTouchCenterRef = useRef(null);
  const touchDragStartRef = useRef(null);
  const touchPositionAtDragStartRef = useRef(null);
  
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.25;

  useEffect(() => {
    if (!trackRef || !videoRef.current) return;

    const videoTrack = trackRef.publication?.track || trackRef.publication?.videoTrack || trackRef.track;
    if (!videoTrack) return;

    if (attachedTrackRef.current === videoTrack) {
      return;
    }

    if (attachedTrackRef.current && videoRef.current) {
      try {
        attachedTrackRef.current.detach(videoRef.current);
      } catch (e) {
        // Silent cleanup
      }
    }

    videoTrack.attach(videoRef.current);
    attachedTrackRef.current = videoTrack;
    
    return () => {
      if (attachedTrackRef.current && videoRef.current) {
        try {
          attachedTrackRef.current.detach(videoRef.current);
        } catch (e) {
          // Silent cleanup
        }
      }
      attachedTrackRef.current = null;
    };
  }, [trackRef?.publication?.trackSid, trackRef?.publication?.track, trackRef?.track]);
  
  const handleReset = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);
  
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);
  
  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      if (newZoom === MIN_ZOOM) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);
  
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
      setZoom(prev => {
        const newZoom = Math.max(MIN_ZOOM, Math.min(prev + delta, MAX_ZOOM));
        if (newZoom === MIN_ZOOM) {
          setPosition({ x: 0, y: 0 });
        }
        return newZoom;
      });
    }
  }, []);
  
  const handleMouseEnter = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    hideControlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 300);
    
    if (isDragging) {
      setIsDragging(false);
    }
  }, [isDragging]);
  
  const handleMouseMove = useCallback((e) => {
    if (showControls) {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
    
    if (isDragging && zoom > 1) {
      const container = containerRef.current;
      const content = contentRef.current;
      if (!container || !content) return;
      
      const containerRect = container.getBoundingClientRect();
      const contentWidth = containerRect.width * zoom;
      const contentHeight = containerRect.height * zoom;
      
      const maxX = Math.max(0, (contentWidth - containerRect.width) / 2);
      const maxY = Math.max(0, (contentHeight - containerRect.height) / 2);
      
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  }, [isDragging, dragStart, zoom, showControls]);
  
  const handleMouseDown = useCallback((e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
      e.preventDefault();
    }
  }, [zoom, position]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // ── Touch handlers for iPad / phone pinch-to-zoom and drag ──────────────

  const getTouchDistance = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      lastTouchDistRef.current = getTouchDistance(e.touches[0], e.touches[1]);
      lastTouchCenterRef.current = getTouchCenter(e.touches[0], e.touches[1]);
    } else if (e.touches.length === 1) {
      // Single finger drag start (only when zoomed)
      touchDragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      touchPositionAtDragStartRef.current = { ...position };
    }
  }, [position]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const newDist = getTouchDistance(e.touches[0], e.touches[1]);
      const prevDist = lastTouchDistRef.current;
      if (!prevDist) return;

      const scale = newDist / prevDist;
      lastTouchDistRef.current = newDist;

      setZoom(prev => {
        const newZoom = Math.max(MIN_ZOOM, Math.min(prev * scale, MAX_ZOOM));
        if (newZoom === MIN_ZOOM) setPosition({ x: 0, y: 0 });
        return newZoom;
      });
    } else if (e.touches.length === 1 && touchDragStartRef.current) {
      // Single finger pan when zoomed in.
      // Capture ref values synchronously here — the setZoom updater runs
      // asynchronously and by then touchDragStartRef.current may be null
      // (handleTouchEnd can fire between the check above and the updater running).
      const dragStart = touchDragStartRef.current;
      const posAtStart = touchPositionAtDragStartRef.current;
      const touch = e.touches[0];
      const container = containerRef.current;

      if (!dragStart || !posAtStart || !container) return;

      const dx = touch.clientX - dragStart.x;
      const dy = touch.clientY - dragStart.y;
      const containerRect = container.getBoundingClientRect();

      setZoom(currentZoom => {
        if (currentZoom <= 1) return currentZoom;
        const maxX = Math.max(0, (containerRect.width * currentZoom - containerRect.width) / 2);
        const maxY = Math.max(0, (containerRect.height * currentZoom - containerRect.height) / 2);
        setPosition({
          x: Math.max(-maxX, Math.min(maxX, posAtStart.x + dx)),
          y: Math.max(-maxY, Math.min(maxY, posAtStart.y + dy)),
        });
        return currentZoom;
      });
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) {
      lastTouchDistRef.current = null;
      lastTouchCenterRef.current = null;
    }
    if (e.touches.length === 0) {
      touchDragStartRef.current = null;
      touchPositionAtDragStartRef.current = null;
    }
  }, []);

  // Register touch handlers with { passive: false } so we can preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);
  
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel, { passive: false });
    };
  }, [handleWheel]);
  
  useEffect(() => {
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, []);
  
  // Zoom controls (+/-/reset) are desktop-only — on touch devices users pinch to zoom
  const isDesktop = getDeviceType() === 'desktop';

  return (
    <div className="relative w-full h-full bg-black">
      <div 
        className={`absolute top-4 right-4 z-20 flex flex-col gap-2 transition-opacity duration-300 ${
          isDesktop && showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-gray-900/90 backdrop-blur rounded-lg p-1 flex flex-col gap-1">
          <button
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-2 text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('videoGrid.zoom.zoomIn')}
          >
            <MagnifyingGlassPlusIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-2 text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('videoGrid.zoom.zoomOut')}
          >
            <MagnifyingGlassMinusIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleReset}
            disabled={zoom === 1}
            className="p-2 text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('videoGrid.zoom.reset')}
          >
            <ArrowsPointingOutIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="bg-gray-900/90 backdrop-blur rounded-lg px-2 py-1">
          <span className="text-white text-xs font-medium">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>
      
      {isDesktop && zoom === 1 && showControls && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-gray-900/90 backdrop-blur rounded-lg px-3 py-2">
            <p className="text-white text-xs">
              {t('videoGrid.zoom.instructions')}
            </p>
          </div>
        </div>
      )}
      
      <div
        ref={containerRef}
        className="w-full h-full overflow-hidden flex items-center justify-center"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        tabIndex={0}
        style={{
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          outline: 'none',
          touchAction: 'none', // prevent browser zoom intercepting pinch on iPad/phone
        }}
      >
        <div
          ref={contentRef}
          className="relative transition-transform duration-200 ease-out"
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
            transformOrigin: 'center',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={false}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              background: '#000'
            }}
          />
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.trackRef?.publication?.trackSid === nextProps.trackRef?.publication?.trackSid;
});

ZoomableScreenShare.displayName = 'ZoomableScreenShare';

// ParticipantOverlay - optimized memoization
// Time in ms that poor quality must persist before showing warning (5 seconds)
const POOR_QUALITY_DEBOUNCE_MS = 5000;

const ParticipantOverlay = memo(({ 
  participant, 
  participantVirtualRooms, 
  virtualRooms,
  updateTrigger,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  isTeacher,
  isDisconnecting
}) => {
  const { t } = useTranslation();
  const [isMuted, setIsMuted] = useState(true);
  const [connectionQuality, setConnectionQuality] = useState(ConnectionQuality.Unknown);
  const [isUnstable, setIsUnstable] = useState(false);
  const cleanupRef = useRef(null);
  const mountedRef = useRef(true);
  const listenerKeysRef = useRef([]);
  const poorQualityTimerRef = useRef(null);
  
  if (!participant) return null;
  
  const metadata = useMemo(() => {
    if (!participant?.metadata) return {};
    try {
      return JSON.parse(participant.metadata);
    } catch {
      return {};
    }
  }, [participant?.metadata]);
  
  const roomInfo = useMemo(() => {
    const roomId = participantVirtualRooms.hasOwnProperty(participant.identity) 
      ? participantVirtualRooms[participant.identity]
      : metadata.virtualRoomId;
    
    if (roomId != null && roomId !== undefined) {
      const room = virtualRooms?.find(r => r.id === roomId);
      return room?.display_name || 
             room?.room_name || 
             globalRoomNamesRef.current.get(roomId) || 
             t('videoGrid.room', { number: room?.room_number || roomId });
    }
    return null;
  }, [participant.identity, participantVirtualRooms, virtualRooms, metadata.virtualRoomId, t]);
  
  useEffect(() => {
    if (!cleanupRef.current) {
      cleanupRef.current = new VideoGridCleanup();
    }
    const cleanup = cleanupRef.current;
    
    listenerKeysRef.current.forEach(key => cleanup.removeListener(key));
    listenerKeysRef.current = [];
    
    mountedRef.current = true;
    
    // Helper to check current mic mute state
    const checkMicMuteState = () => {
      const micPub = participant.audioTrackPublications ? 
        Array.from(participant.audioTrackPublications.values()).find(
          (pub) => pub.source === Track.Source.Microphone
        ) : null;
      
      if (mountedRef.current) {
        setIsMuted(micPub ? !!micPub.isMuted : true);
      }
    };
    
    // Check initial state
    checkMicMuteState();
    
    // Check initial connection quality
    if (participant.connectionQuality !== undefined && mountedRef.current) {
      setConnectionQuality(participant.connectionQuality);
    }
    
    const handleTrackMuted = (publication) => {
      if (publication.source === Track.Source.Microphone && mountedRef.current) {
        setIsMuted(true);
      }
    };
    
    const handleTrackUnmuted = (publication) => {
      if (publication.source === Track.Source.Microphone && mountedRef.current) {
        setIsMuted(false);
      }
    };
    
    // Handle track published/subscribed - recheck mute state when mic track arrives
    const handleTrackPublished = (publication) => {
      if (publication.source === Track.Source.Microphone && mountedRef.current) {
        // When mic track is published, check its mute state
        setIsMuted(!!publication.isMuted);
      }
    };
    
    const handleTrackSubscribed = (track, publication) => {
      if (publication.source === Track.Source.Microphone && mountedRef.current) {
        // When mic track is subscribed, check its mute state
        setIsMuted(!!publication.isMuted);
      }
    };
    
    // Handle connection quality changes with debounce for Poor quality
    const handleConnectionQualityChanged = (quality) => {
      if (!mountedRef.current) return;
      
      setConnectionQuality(quality);
      
      // Handle Poor quality with 5-second debounce
      if (quality === ConnectionQuality.Poor) {
        // Start timer if not already running
        if (!poorQualityTimerRef.current) {
          poorQualityTimerRef.current = setTimeout(() => {
            if (mountedRef.current) {
              setIsUnstable(true);
            }
            poorQualityTimerRef.current = null;
          }, POOR_QUALITY_DEBOUNCE_MS);
        }
      } else {
        // Quality changed from Poor - clear timer and reset unstable state
        if (poorQualityTimerRef.current) {
          clearTimeout(poorQualityTimerRef.current);
          poorQualityTimerRef.current = null;
        }
        // Only clear unstable if quality improved (not if it became Lost)
        if (quality !== ConnectionQuality.Lost) {
          setIsUnstable(false);
        }
      }
    };
    
    const keys = [
      cleanup.addListener(participant, 'trackMuted', handleTrackMuted),
      cleanup.addListener(participant, 'trackUnmuted', handleTrackUnmuted),
      cleanup.addListener(participant, 'trackPublished', handleTrackPublished),
      cleanup.addListener(participant, 'trackSubscribed', handleTrackSubscribed),
      cleanup.addListener(participant, 'connectionQualityChanged', handleConnectionQualityChanged),
    ].filter(Boolean);
    
    listenerKeysRef.current = keys;

    return () => {
      mountedRef.current = false;
      keys.forEach(key => cleanup.removeListener(key));
      // Clear poor quality timer on cleanup
      if (poorQualityTimerRef.current) {
        clearTimeout(poorQualityTimerRef.current);
        poorQualityTimerRef.current = null;
      }
    };
  }, [participant]);
  
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (cleanupRef.current) {
        cleanupRef.current.cleanup();
        cleanupRef.current = null;
      }
      // Clear poor quality timer on unmount
      if (poorQualityTimerRef.current) {
        clearTimeout(poorQualityTimerRef.current);
        poorQualityTimerRef.current = null;
      }
    };
  }, []);
  
  const name = metadata.displayName || participant.name || t('videoGrid.unknown');
  const isTeacherRole = metadata.role === 'teacher';
  
  // Connection status flags
  const isLostConnection = connectionQuality === ConnectionQuality.Lost;
  const isUnstableConnection = isUnstable && !isLostConnection;
  
  // Determine the participant's virtual room ID
  const participantRoomId = participantVirtualRooms.hasOwnProperty(participant.identity) 
    ? participantVirtualRooms[participant.identity]
    : metadata.virtualRoomId;
  
  // Check if teacher is broadcasting/monitoring this participant's room
  const isBroadcastingTo = participantRoomId && teacherBroadcastRooms && teacherBroadcastRooms.has(participantRoomId);
  const isMonitoringRoom = participantRoomId && teacherMonitorRooms && teacherMonitorRooms.has(participantRoomId);
  const isSilentMonitoringRoom = participantRoomId && teacherMonitorSilentRooms && teacherMonitorSilentRooms.has(participantRoomId);
  
  
  return (
    <>
    <div className="participant-overlay absolute bottom-2 left-2 z-10 flex items-center gap-2 pointer-events-none flex-wrap">
      <span className="bg-black/75 text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2">
        {name}
        {isMuted && (
          <SpeakerXMarkIcon className="w-4 h-4 text-red-500" />
        )}
      </span>
      {/* Connection lost warning badge (red) */}
      {isLostConnection && (
        <span 
          className="bg-red-600/90 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1"
          title={t('liveSession.connection.participantLost', 'Connection lost')}
        >
          <SignalSlashIcon className="w-3.5 h-3.5" />
          {t('liveSession.connection.lost', 'Lost')}
        </span>
      )}
      {/* Connection unstable warning badge (yellow) */}
      {isUnstableConnection && (
        <span 
          className="bg-yellow-500/90 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1"
          title={t('liveSession.connection.participantPoor', 'Unstable connection')}
        >
          <ExclamationTriangleIcon className="w-3.5 h-3.5" />
          {t('liveSession.connection.unstable', 'Unstable')}
        </span>
      )}
      {isTeacherRole && (
        <span className="bg-cyan-600/90 text-white px-2 py-1 rounded text-xs font-semibold">
          {t('roles.teacher')}
        </span>
      )}
      {isDisconnecting && (
        <span 
          className="bg-orange-500/90 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1"
          title={t('liveSession.connection.disconnectedBadge', 'Disconnected')}
        >
          <SignalSlashIcon className="w-3.5 h-3.5" />
          {t('liveSession.connection.disconnectedBadge', 'Disconnected')}
        </span>
      )}
      {roomInfo && (
        <span className="bg-purple-600/90 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1.5">
          {roomInfo}
          {/* Teacher monitoring icons */}
          {isBroadcastingTo && isTeacher && (
            <span 
              className="inline-flex items-center justify-center" 
              title={t('liveSession.participants.joinedRoom')}
            >
              <PlusIcon className="w-3.5 h-3.5 text-white stroke-2" />
            </span>
          )}
          {isMonitoringRoom && isTeacher && (
            <span 
              className="inline-flex items-center justify-center" 
              title={t('liveSession.participants.monitoring')}
            >
              <VideoCameraIcon className="w-3.5 h-3.5 text-white stroke-2" />
            </span>
          )}
          {isSilentMonitoringRoom && isTeacher && (
            <span 
              className="inline-flex items-center justify-center" 
              title={t('liveSession.participants.silentMonitoring')}
            >
              <SpeakerXMarkIcon className="w-3.5 h-3.5 text-white stroke-2" />
            </span>
          )}
        </span>
      )}
    </div>
    {/* Connection lost border indicator (red, pulsing) */}
    {isLostConnection && (
      <div 
        className="absolute inset-0 rounded-lg pointer-events-none z-0 ring-2 ring-red-500 ring-inset animate-pulse"
      />
    )}
    {/* Connection unstable border indicator (yellow) */}
    {isUnstableConnection && (
      <div 
        className="absolute inset-0 rounded-lg pointer-events-none z-0 ring-2 ring-yellow-500 ring-inset"
      />
    )}
    {/* Disconnecting: dimmed with orange border */}
    {isDisconnecting && (
      <>
        <div className="absolute inset-0 rounded-lg pointer-events-none z-0 ring-2 ring-orange-500 ring-inset" />
        <div className="absolute inset-0 rounded-lg pointer-events-none z-0 bg-black/40" />
      </>
    )}
    </>
  );
}, (prevProps, nextProps) => {
  return prevProps.participant?.identity === nextProps.participant?.identity &&
         prevProps.updateTrigger === nextProps.updateTrigger &&
         prevProps.isDisconnecting === nextProps.isDisconnecting;
});

ParticipantOverlay.displayName = 'ParticipantOverlay';

// LiveKitTile: uses LiveKit's ParticipantTile for full track lifecycle management.
// For disconnected placeholder tiles (grace period), renders initials avatar.
const LiveKitTile = memo(({ trackKey }) => {
  const trackRef = useContext(TrackRefContext);
  const participant = trackRef?.participant;
  const isSynthetic = trackRef?.isSyntheticPlaceholder;
  const isDisconnectedPlaceholder = trackRef?.isDisconnectedPlaceholder;

  // Disconnected placeholder: no real LiveKit participant, render initials
  if (isDisconnectedPlaceholder || (isSynthetic && !participant?.trackPublications?.size)) {
    let displayName = '';
    try {
      if (participant?.metadata) {
        const meta = JSON.parse(participant.metadata);
        displayName = meta.displayName || '';
      }
    } catch (e) {}
    displayName = displayName || participant?.name || '';
    
    const initials = (() => {
      if (!displayName) return '?';
      const parts = displayName.trim().split(/\s+/);
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      return displayName[0]?.toUpperCase() || '?';
    })();
    
    return (
      <div className="lk-participant-tile" style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', borderRadius: '8px', background: '#111827' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 600, color: '#9CA3AF' }}>
            {initials}
          </div>
        </div>
      </div>
    );
  }

  // Real participant: let LiveKit handle everything (video, avatar, mute/unmute, republish)
  return <ParticipantTile />;
}, (prevProps, nextProps) => {
  return prevProps.trackKey === nextProps.trackKey;
});

LiveKitTile.displayName = 'LiveKitTile';

// OPTIMIZED: PiP Button with auto-exit and no memory leaks
const PiPButton = memo(({ trackRef }) => {
  const { t } = useTranslation();
  const [isPiPActive, setIsPiPActive] = useState(false);
  const pipCheckRef = useRef(null);
  const videoElementRef = useRef(null);
  const mountedRef = useRef(true);

  // PiP is desktop-only — not supported on iOS/iPadOS and not useful on phones/tablets
  const deviceType = getDeviceType();
  if (deviceType !== 'desktop') return null;
  
  const handlePiP = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!trackRef || !mountedRef.current) return;
    
    const videoTrack = trackRef.publication?.track || trackRef.publication?.videoTrack;
    if (!videoTrack) return;
    
    const videoElements = document.querySelectorAll('video');
    let targetVideo = null;
    
    for (const video of videoElements) {
      if (video.srcObject) {
        const stream = video.srcObject;
        const tracks = stream.getVideoTracks();
        if (tracks.some(t => t.id === videoTrack.mediaStreamTrack.id)) {
          targetVideo = video;
          break;
        }
      }
    }
    
    if (!targetVideo) return;
    
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        if (mountedRef.current) {
          setIsPiPActive(false);
          videoElementRef.current = null;
        }
      } else {
        targetVideo.requestPictureInPicture().then(() => {
          if (mountedRef.current) {
            setIsPiPActive(true);
            videoElementRef.current = targetVideo;
          }
        }).catch((error) => {
          console.error('PiP error:', error);
          if (mountedRef.current) {
            videoElementRef.current = null;
          }
        });
      }
    } catch (error) {
      console.error('PiP error:', error);
      if (mountedRef.current) {
        videoElementRef.current = null;
      }
    }
  }, [trackRef]);
  
  // Monitor video track and auto-exit PiP if track ends
  useEffect(() => {
    if (!trackRef) return;
    
    const videoTrack = trackRef.publication?.track || trackRef.publication?.videoTrack;
    if (!videoTrack) return;
    
    const mediaStreamTrack = videoTrack.mediaStreamTrack;
    if (!mediaStreamTrack) return;
    
    const handleTrackEnded = async () => {
      if (document.pictureInPictureElement && videoElementRef.current) {
        try {
          await document.exitPictureInPicture();
        } catch (e) {
          // Silent exit
        }
      }
      if (mountedRef.current) {
        setIsPiPActive(false);
        videoElementRef.current = null;
      }
    };
    
    mediaStreamTrack.addEventListener('ended', handleTrackEnded);
    
    return () => {
      mediaStreamTrack.removeEventListener('ended', handleTrackEnded);
    };
  }, [trackRef]);
  
  // Monitor PiP state and video element health
  useEffect(() => {
    const checkPiPState = async () => {
      if (!mountedRef.current) return;
      
      const isInPiP = document.pictureInPictureElement !== null;
      
      if (isPiPActive && !isInPiP) {
        setIsPiPActive(false);
        videoElementRef.current = null;
      }
      
      if (isInPiP && videoElementRef.current) {
        const video = videoElementRef.current;
        
        if (!video.srcObject || video.readyState === 0) {
          try {
            await document.exitPictureInPicture();
          } catch (e) {
            // Silent exit
          }
          if (mountedRef.current) {
            setIsPiPActive(false);
            videoElementRef.current = null;
          }
        }
      }
    };
    
    pipCheckRef.current = setInterval(checkPiPState, 1000);
    
    const handlePiPEnter = () => {
      if (mountedRef.current) {
        setIsPiPActive(true);
      }
    };
    
    const handlePiPExit = () => {
      if (mountedRef.current) {
        setIsPiPActive(false);
        videoElementRef.current = null;
      }
    };
    
    document.addEventListener('enterpictureinpicture', handlePiPEnter, { passive: true });
    document.addEventListener('leavepictureinpicture', handlePiPExit, { passive: true });
    
    return () => {
      if (pipCheckRef.current) {
        clearInterval(pipCheckRef.current);
        pipCheckRef.current = null;
      }
      document.removeEventListener('enterpictureinpicture', handlePiPEnter);
      document.removeEventListener('leavepictureinpicture', handlePiPExit);
    };
  }, [isPiPActive]);
  
  // Clean up on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      
      if (document.pictureInPictureElement && videoElementRef.current) {
        try {
          document.exitPictureInPicture().catch(() => {});
        } catch (e) {
          // Silent cleanup
        }
      }
      
      videoElementRef.current = null;
      
      if (pipCheckRef.current) {
        clearInterval(pipCheckRef.current);
        pipCheckRef.current = null;
      }
    };
  }, []);
  
  return (
    <button
      onClick={handlePiP}
      onMouseDown={(e) => e.stopPropagation()}
      className={`pip-button absolute top-2 right-2 z-20 p-2 rounded-lg transition-all opacity-0 pointer-events-auto ${
        isPiPActive
          ? 'bg-cyan-600 text-white'
          : 'bg-black/60 text-white hover:bg-black/80'
      }`}
      title={isPiPActive ? t('videoGrid.pip.exit') : t('videoGrid.pip.open')}
      tabIndex={-1}
    >
      {/* YouTube-style PiP icon: large rectangle with small rectangle in bottom-right corner */}
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="3" width="18" height="14" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="10" y="9" width="8" height="5.5" rx="1" fill="currentColor"/>
      </svg>
    </button>
  );
}, (prevProps, nextProps) => {
  return prevProps.trackRef?.publication?.trackSid === nextProps.trackRef?.publication?.trackSid;
});

PiPButton.displayName = 'PiPButton';

const CustomParticipantTile = memo(({ 
  participantVirtualRooms,
  virtualRooms,
  trackKey,
  updateTrigger,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  isTeacher,
  gracePeriodParticipants,
  ...props 
}) => {
  const trackRef = useContext(TrackRefContext);
  const participant = trackRef?.participant;
  
  if (!participant) return <LiveKitTile trackKey={trackKey} />;
  
  const isDisconnecting = gracePeriodParticipants?.has(participant.identity);
  
  return (
    <div className={`relative h-full w-full pip-container ${isDisconnecting ? 'tile-disconnecting' : ''}`} data-track-key={trackKey}>
      <LiveKitTile trackKey={trackKey} />
      <ParticipantOverlay 
        participant={participant}
        participantVirtualRooms={participantVirtualRooms}
        virtualRooms={virtualRooms}
        updateTrigger={updateTrigger}
        teacherBroadcastRooms={teacherBroadcastRooms}
        teacherMonitorRooms={teacherMonitorRooms}
        teacherMonitorSilentRooms={teacherMonitorSilentRooms}
        isTeacher={isTeacher}
        isDisconnecting={isDisconnecting}
      />
      <PiPButton trackRef={trackRef} />
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.trackKey === nextProps.trackKey &&
         prevProps.updateTrigger === nextProps.updateTrigger &&
         prevProps.gracePeriodParticipants === nextProps.gracePeriodParticipants;
});

CustomParticipantTile.displayName = 'CustomParticipantTile';

// Sidebar for presentation mode with pagination
// When horizontal=true (phone layout), renders as a top strip instead of a right column
const PresentationSidebar = memo(({ 
  tracks, 
  participantVirtualRooms, 
  virtualRooms, 
  updateTrigger,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  isTeacher,
  gracePeriodParticipants,
  horizontal = false,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const ITEMS_PER_PAGE = 12;
  
  const totalPages = Math.ceil(tracks.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentTracks = useMemo(() => tracks.slice(startIndex, endIndex), [tracks, startIndex, endIndex]);
  
  // Horizontal (phone) strip: fixed height row at top, cameras scroll left-right
  if (horizontal) {
    return (
      <div
        className="flex flex-row gap-1.5 overflow-x-auto flex-shrink-0 bg-gray-900/80"
        style={{ height: 80, padding: '4px 6px' }}
      >
        {tracks.map((track) => {
          const trackKey = `${track.participant?.identity || track.participant?.sid}-${track.source}`;
          return (
            <div key={trackKey} className="flex-shrink-0 rounded overflow-hidden" style={{ width: 106, height: 72 }}>
              <TrackRefContext.Provider value={track}>
                <CustomParticipantTile
                  trackKey={trackKey}
                  participantVirtualRooms={participantVirtualRooms}
                  virtualRooms={virtualRooms}
                  updateTrigger={updateTrigger}
                  teacherBroadcastRooms={teacherBroadcastRooms}
                  teacherMonitorRooms={teacherMonitorRooms}
                  teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                  isTeacher={isTeacher}
                  gracePeriodParticipants={gracePeriodParticipants}
                />
              </TrackRefContext.Provider>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="participants-sidebar">
      {totalPages > 1 && (
        <div className="flex items-center justify-between mb-2 px-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="p-1 text-white hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <span className="text-xs text-gray-400">
            {t('videoGrid.pagination.page', { current: currentPage + 1, total: totalPages })}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage === totalPages - 1}
            className="p-1 text-white hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {currentTracks.map((track) => {
          // Stable key: identity+source never changes (trackSid appears async causing remount flash)
          const trackKey = `${track.participant?.identity || track.participant?.sid}-${track.source}`;
          return (
            <div key={trackKey} className="w-full aspect-video">
              <TrackRefContext.Provider value={track}>
                <CustomParticipantTile
                  trackKey={trackKey}
                  participantVirtualRooms={participantVirtualRooms}
                  virtualRooms={virtualRooms}
                  updateTrigger={updateTrigger}
                  teacherBroadcastRooms={teacherBroadcastRooms}
                  teacherMonitorRooms={teacherMonitorRooms}
                  teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                  isTeacher={isTeacher}
                  gracePeriodParticipants={gracePeriodParticipants}
                />
              </TrackRefContext.Provider>
            </div>
          );
        })}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.tracks.length === nextProps.tracks.length &&
         prevProps.updateTrigger === nextProps.updateTrigger;
});

PresentationSidebar.displayName = 'PresentationSidebar';

// Smart Grid that uses dynamic CSS grid with pagination
const DynamicGridLayout = memo(({ 
  tracks, 
  children, 
  participantVirtualRooms, 
  virtualRooms, 
  updateTrigger,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  teacherMonitorSilentRooms,
  isTeacher,
  gracePeriodParticipants
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [gridLayout, setGridLayout] = useState({ cols: 1, rows: 1 });
  const [currentPage, setCurrentPage] = useState(0);
  
  const ITEMS_PER_PAGE = 12;
  const totalPages = Math.ceil(tracks.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentTracks = useMemo(() => tracks.slice(startIndex, endIndex), [tracks, startIndex, endIndex]);
  
  useEffect(() => {
    const updateLayout = () => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const layout = calculateOptimalGrid(rect.width, rect.height, currentTracks.length);
      setGridLayout(layout);
    };
    
    updateLayout();
    
    const resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        updateLayout();
      });
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [currentTracks.length]);
  
  useEffect(() => {
    if (currentPage >= totalPages && totalPages > 0) {
      setCurrentPage(totalPages - 1);
    }
  }, [totalPages, currentPage]);
  
  return (
    <div className="relative w-full h-full">
      {totalPages > 1 && (
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-30 bg-gray-900/90 backdrop-blur rounded-lg px-3 py-1.5 flex items-center gap-3">
          <button
            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="p-1 text-white hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="text-sm text-white font-medium">
            {t('videoGrid.pagination.page', { current: currentPage + 1, total: totalPages })}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage === totalPages - 1}
            className="p-1 text-white hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}
      
      <div 
        ref={containerRef}
        className="dynamic-grid-layout"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridLayout.cols}, 1fr)`,
          gridTemplateRows: `repeat(${gridLayout.rows}, 1fr)`,
          gap: '0.5rem',
          padding: '0.5rem',
          width: '100%',
          height: '100%',
          background: '#000'
        }}
      >
        {currentTracks.map((track) => {
          // Stable key: identity+source never changes (trackSid appears async causing remount flash)
          const trackKey = `${track.participant?.identity || track.participant?.sid}-${track.source}`;
          return (
            <TrackRefContext.Provider key={trackKey} value={track}>
              <CustomParticipantTile
                trackKey={trackKey}
                participantVirtualRooms={participantVirtualRooms}
                virtualRooms={virtualRooms}
                updateTrigger={updateTrigger}
                teacherBroadcastRooms={teacherBroadcastRooms}
                teacherMonitorRooms={teacherMonitorRooms}
                teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                isTeacher={isTeacher}
                gracePeriodParticipants={gracePeriodParticipants}
              />
            </TrackRefContext.Provider>
          );
        })}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Only re-render if the actual set of participants changed, or updateTrigger changed
  // (updateTrigger is needed for overlay updates like room badges)
  if (prevProps.updateTrigger !== nextProps.updateTrigger) return false;
  if (prevProps.tracks.length !== nextProps.tracks.length) return false;
  // Same length — check if same participants in same order
  for (let i = 0; i < prevProps.tracks.length; i++) {
    const prevId = prevProps.tracks[i]?.participant?.identity;
    const nextId = nextProps.tracks[i]?.participant?.identity;
    if (prevId !== nextId) return false;
  }
  return true;
});

DynamicGridLayout.displayName = 'DynamicGridLayout';

const VideoGrid = ({
  layoutMode = 'grid',
  screenSharers = [],
  embeddedDocument = null,
  documentSharer = null,
  localParticipantIdentity = null,
  virtualRooms = [],
  participantVirtualRooms = {},
  teacherBroadcastRooms = new Set(),
  teacherMonitorRooms = new Set(),
  teacherMonitorSilentRooms = new Set(), // FIXED: Added silent monitoring rooms
  remoteTeacherStates = {},
  currentVirtualRoom = null,
  isTeacher = false,
  hideOwnVideo = false,
  whiteboardComponent = null,
  isWhiteboardActive = false,
  isWhiteboardDetached = false,
  gracePeriodParticipants = new Map(),
}) => {
  const { t } = useTranslation();
  const [docLoading, setDocLoading] = useState(true);
  const [authCheckAttempts, setAuthCheckAttempts] = useState(0);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [activeScreenShareIndex, setActiveScreenShareIndex] = useState(0);
  const room = useRoomContext();
  
  const cleanupRef = useRef(new VideoGridCleanup());
  const cleanup = cleanupRef.current;
  const styleRef = useRef(null);
  const mountedRef = useRef(true);
  const trackCacheRef = useRef(new Map());

  // Use LiveKit's built-in useTracks — reactive, self-healing, never misses track events.
  // onlySubscribed: true means we only see tracks managed by our subscription engine.
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: true }
  );

  useEffect(() => {
    if (!room) return;
    
    // Camera tracks are managed by useTracks (reactive). These debounced timers
    // serve the PLACEHOLDER section (participants without camera who show as initials).
    let connectDebounce = null;
    let disconnectDebounce = null;
    
    const handleParticipantConnected = () => {
      // Wait for tracks to arrive before showing placeholder
      clearTimeout(connectDebounce);
      connectDebounce = setTimeout(() => {
        setUpdateTrigger(prev => prev + 1);
      }, 800);
    };
    
    const handleParticipantDisconnected = () => {
      // Quick update to remove placeholder
      clearTimeout(disconnectDebounce);
      disconnectDebounce = setTimeout(() => {
        setUpdateTrigger(prev => prev + 1);
      }, 50);
    };
    
    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);
    
    return () => {
      clearTimeout(connectDebounce);
      clearTimeout(disconnectDebounce);
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
    };
  }, [room]);

  // Near-instant room assignment change trigger — for placeholder section.
  const roomChangeDebounceRef = useRef(null);
  useEffect(() => {
    clearTimeout(roomChangeDebounceRef.current);
    roomChangeDebounceRef.current = setTimeout(() => {
      setUpdateTrigger(prev => prev + 1);
    }, 50);
    return () => clearTimeout(roomChangeDebounceRef.current);
  }, [participantVirtualRooms, currentVirtualRoom]);

  useEffect(() => {
    virtualRooms.forEach(room => {
      const name = room.display_name || room.room_name || t('videoGrid.room', { number: room.room_number || '?' });
      globalRoomNamesRef.current.set(room.id, name);
    });
    
    const validIds = new Set(virtualRooms.map(r => r.id));
    for (const [id] of globalRoomNamesRef.current) {
      if (!validIds.has(id)) {
        globalRoomNamesRef.current.delete(id);
      }
    }
  }, [virtualRooms, t]);

  const depsHash = useMemo(() => {
    return {
      broadcastRooms: Array.from(teacherBroadcastRooms).sort().join(','),
      monitorRooms: Array.from(teacherMonitorRooms).sort().join(','),
      monitorSilentRooms: Array.from(teacherMonitorSilentRooms).sort().join(','), // FIXED: Added silent monitoring
      participantRooms: Object.entries(participantVirtualRooms)
        .map(([k, v]) => `${k}:${v}`)
        .sort()
        .join(','),
      currentRoom: currentVirtualRoom?.id || 'main',
      updateTrigger: updateTrigger,
      remoteTeacherStates: JSON.stringify(remoteTeacherStates)
    };
  }, [teacherBroadcastRooms, teacherMonitorRooms, teacherMonitorSilentRooms, participantVirtualRooms, currentVirtualRoom, updateTrigger, remoteTeacherStates]);

  const { visibleCameraTracks, visibleScreenShares } = useMemo(() => {
    // Get my room ID - prefer currentVirtualRoom prop, but fall back to local participant metadata
    let myRoomId = currentVirtualRoom?.id || null;
    
    // If currentVirtualRoom isn't set yet, check local participant's metadata
    if (myRoomId === null && room?.localParticipant?.metadata) {
      try {
        const localMetadata = JSON.parse(room.localParticipant.metadata);
        if (localMetadata.virtualRoomId !== undefined && localMetadata.virtualRoomId !== null) {
          myRoomId = localMetadata.virtualRoomId;
        }
      } catch (e) {}
    }
    
    const camera = [];
    const screen = [];

    // === TRACK VISIBILITY ===
    // useTracks returns all subscribed tracks. Guard against intermediate states
    // where publication or track object hasn't fully materialized yet.
    tracks.forEach(track => {
      const participant = track.participant;
      if (!participant) return;
      
      // Guard: must have a publication to render
      if (!track.publication) return;
      
      const isMe = participant.identity === localParticipantIdentity || 
                   (room && participant === room.localParticipant);
      
      // For remote tracks, require trackSid (means fully subscribed)
      // For local tracks, require the track object (means actively publishing)
      if (!isMe && !track.publication.trackSid) return;
      if (isMe && !track.publication.track) return;
      
      const trackKey = `${participant.identity || participant.sid}-${track.source}`;

      if (track.source === Track.Source.Camera) {
        // Skip unsubscribed remote tracks, but always show local tracks
        if (!isMe && !track.publication?.isSubscribed) return;
        if (isMe && hideOwnVideo) return;
        trackCacheRef.current.set(trackKey, track);
        camera.push(track);
      } else if (track.source === Track.Source.ScreenShare) {
        // Skip whiteboard egress track — students get interactive WhiteboardCanvas
        if (track.publication?.trackName === 'whiteboard') return;
        // Skip unsubscribed remote screen shares
        if (!isMe && !track.publication?.isSubscribed) return;
        if (isMe) return; // Don't show own screen share
        screen.push(track);
      }
    });
    
    // Add placeholders for participants who NEVER published a camera track
    if (room) {
      const participantsInCamera = new Set(camera.map(t => t.participant.identity));
      
      // Check if local participant metadata indicates we SHOULD be in a breakout room
      let localMetadataRoom = null;
      let localMetadataReady = false;
      if (room.localParticipant?.metadata) {
        try {
          const localMeta = JSON.parse(room.localParticipant.metadata);
          localMetadataRoom = localMeta.virtualRoomId;
          localMetadataReady = 'virtualRoomId' in localMeta; // Metadata has room info
        } catch (e) {}
      }
      
      // For students: skip placeholders if we don't know our room yet
      // Either metadata isn't ready, or there's a mismatch between metadata and currentVirtualRoom
      const effectiveMyRoom = myRoomId !== null ? myRoomId : localMetadataRoom;
      
      if (!isTeacher && !localMetadataReady) {
        console.log('[VideoGrid] Skipping placeholders - student metadata not ready');
        // Don't create any placeholders yet - wait for our own metadata
      } else if (!isTeacher && localMetadataRoom !== null && myRoomId === null) {
        console.log('[VideoGrid] Skipping placeholders - student room state inconsistent', {
          myRoomId,
          localMetadataRoom
        });
        // Don't create any placeholders yet
      } else {
      
      // Use the effective room ID for placeholder decisions
      const placeholderMyRoom = effectiveMyRoom;
      
      // Helper to check if participant has actually published a camera track
      const hasPublishedCamera = (participant) => {
        for (const pub of participant.trackPublications.values()) {
          if (pub.source === Track.Source.Camera) {
            return true;
          }
        }
        return false;
      };
      
      const shouldParticipantShow = (participant) => {
        let metadata = {};
        try {
          if (participant.metadata) metadata = JSON.parse(participant.metadata);
        } catch (e) {}

        // Don't show placeholder until we have metadata - wait for it to load
        // This prevents showing placeholders for participants in wrong rooms
        if (!participant.metadata || !metadata.role) {
          return false;
        }

        const participantRoomId = participantVirtualRooms[participant.identity] !== undefined ? 
          participantVirtualRooms[participant.identity] : 
          metadata.virtualRoomId;
          
        const normalizedParticipantRoom = (participantRoomId === undefined || participantRoomId === null) ? null : participantRoomId;
        const normalizedMyRoom = (placeholderMyRoom === undefined || placeholderMyRoom === null) ? null : placeholderMyRoom;
        
        const participantIsTeacher = metadata.role === 'teacher';
        const isMe = participant.identity === localParticipantIdentity;

        if (isTeacher) {
          if (isMe) return true;
          if (normalizedParticipantRoom === null) return true;
          
          const isMonitoring = Array.from(teacherMonitorRooms).some(id => 
            Number(id) === Number(normalizedParticipantRoom));
          const isBroadcasting = Array.from(teacherBroadcastRooms).some(id => 
            Number(id) === Number(normalizedParticipantRoom));
          const isSilentMonitoring = Array.from(teacherMonitorSilentRooms).some(id => 
            Number(id) === Number(normalizedParticipantRoom));
          return isMonitoring || isBroadcasting || isSilentMonitoring;
        } else {
          // STUDENT LOGIC
          if (isMe) return true;
          
          if (participantIsTeacher) {
            // Student viewing teacher — use remoteTeacherStates (WebSocket) as primary,
            // fall back to LiveKit metadata for initial load
            const teacherState = remoteTeacherStates[participant.identity];
            const teacherBroadcasting = teacherState?.broadcastingTo 
              || metadata.broadcastingTo;
            
            if (!teacherBroadcasting) {
              return false;
            }
            
            if (normalizedMyRoom === null) {
              // Student in main room: teacher ALWAYS broadcasts to main
              return true;
            } else {
              // Student in breakout room: teacher must be broadcasting to that room
              return teacherBroadcasting.some(roomId => Number(roomId) === Number(normalizedMyRoom));
            }
          } else {
            // Student viewing other student
            if (normalizedMyRoom === null) {
              return normalizedParticipantRoom === null;
            } else {
              return normalizedParticipantRoom !== null && 
                     Number(normalizedParticipantRoom) === Number(normalizedMyRoom);
            }
          }
        }
      };
      
      // Remote participants - only if never published camera
      for (const [, participant] of room.remoteParticipants) {
        if (participantsInCamera.has(participant.identity)) continue;
        // Check actual track publications, not just what we're subscribed to
        if (hasPublishedCamera(participant)) continue;
        
        if (shouldParticipantShow(participant)) {
          const syntheticTrack = {
            participant: participant,
            source: Track.Source.Camera,
            publication: undefined,
            isSyntheticPlaceholder: true
          };
          camera.push(syntheticTrack);
        }
      }
      
      // Local participant - DON'T create placeholder, just wait for real track
      // The user knows if they have a camera or not, and placeholders cause race conditions
      // during the initial publish phase
      } // End of else block for consistent room state
    }
    
    // Grace period placeholders: participants who disconnected but are still in grace
    // period get a synthetic "disconnected" tile so the grid slot stays occupied.
    // This prevents flicker during browser refreshes (old connection dies, new arrives
    // ~100-500ms later). The tile shows dimmed with "Disconnected" badge.
    if (gracePeriodParticipants.size > 0) {
      const identitiesInCamera = new Set(camera.map(t => t.participant?.identity));
      for (const [identity, info] of gracePeriodParticipants) {
        if (identitiesInCamera.has(identity)) continue; // Already showing via real track
        
        // Create a minimal fake participant object for LiveKitTile's initials
        const fakeParticipant = {
          identity,
          sid: `grace-${identity}`,
          name: info.displayName,
          metadata: info.metadata,
          isLocal: false,
          trackPublications: new Map(),
          audioTrackPublications: new Map(),
          getTrackPublication: () => null,
          on: () => {},
          off: () => {},
        };
        
        camera.push({
          participant: fakeParticipant,
          source: Track.Source.Camera,
          publication: undefined,
          isSyntheticPlaceholder: true,
          isDisconnectedPlaceholder: true,
        });
      }
    }
    
    camera.sort((a, b) => (a.participant?.identity || '').localeCompare(b.participant?.identity || ''));
    
    return { visibleCameraTracks: camera, visibleScreenShares: screen };
  }, [
    tracks,
    room,
    room?.localParticipant?.metadata,
    depsHash.broadcastRooms,
    depsHash.monitorRooms,
    depsHash.monitorSilentRooms,
    depsHash.participantRooms,
    depsHash.currentRoom,
    depsHash.updateTrigger,
    depsHash.remoteTeacherStates,
    localParticipantIdentity, 
    isTeacher, 
    hideOwnVideo,
    gracePeriodParticipants
  ]);

  useEffect(() => {
    if (activeScreenShareIndex >= visibleScreenShares.length && visibleScreenShares.length > 0) {
      setActiveScreenShareIndex(0);
    }
  }, [visibleScreenShares.length, activeScreenShareIndex]);

  useEffect(() => {
    const activeKeys = new Set(
      tracks
        .filter(t => t.participant?.sid && t.publication?.trackSid)
        .map(t => `${t.participant.sid}-${t.publication.trackSid}`)
    );
    
    // Also add local track keys (which might not have trackSid yet)
    tracks
      .filter(t => t.participant?.sid && !t.publication?.trackSid)
      .forEach(t => activeKeys.add(`${t.participant.sid}-local-${t.source}`));
    
    // Keep placeholder keys for connected participants
    if (room) {
      for (const [, participant] of room.remoteParticipants) {
        activeKeys.add(`${participant.sid}-placeholder`);
      }
      if (room.localParticipant) {
        activeKeys.add(`${room.localParticipant.sid}-placeholder`);
      }
    }
    
    for (const [key] of trackCacheRef.current) {
      if (!activeKeys.has(key)) {
        trackCacheRef.current.delete(key);
      }
    }
  }, [tracks, room]);

  const TileComponent = useCallback((props) => {
    const trackRef = props.trackRef || props.trackReference;
    const trackKey = trackRef ? 
      `${trackRef.participant?.identity || trackRef.participant?.sid}-${trackRef.source}` : 
      'unknown';
    
    return (
      <CustomParticipantTile 
        key={trackKey}
        trackKey={trackKey}
        participantVirtualRooms={participantVirtualRooms}
        virtualRooms={virtualRooms}
        updateTrigger={updateTrigger}
        gracePeriodParticipants={gracePeriodParticipants}
        {...props}
      />
    );
  }, [participantVirtualRooms, virtualRooms, updateTrigger, gracePeriodParticipants]);

  const handleIframeLoad = useCallback((e) => {
    if (!mountedRef.current) return;
    
    setDocLoading(false);
    
    cleanup.setTimeout(() => {
      if (!mountedRef.current) return;
      
      try {
        const iframe = e.target;
        if (iframe.contentWindow) {
          // Auth check succeeded
        }
      } catch {
        if (authCheckAttempts === 0) {
          setAuthCheckAttempts(1);
          cleanup.setTimeout(() => {
            if (!mountedRef.current) return;
            
            const iframe = document.querySelector('.document-iframe');
            if (iframe && !iframe.contentDocument) {
              toast.info(t('videoGrid.document.authNotice'), { duration: 5000 });
            }
          }, 2000);
        }
      }
    }, 1000);
  }, [authCheckAttempts, cleanup, t]);

  useEffect(() => {
    if (embeddedDocument) {
      setDocLoading(true);
      setAuthCheckAttempts(0);
    }
  }, [embeddedDocument]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* === SMOOTH GRID TRANSITIONS === */
      .dynamic-grid-layout {
        transition: grid-template-columns 300ms ease, grid-template-rows 300ms ease;
      }
      .dynamic-grid-layout > div {
        container-type: size;
        container-name: video-tile;
        animation: tile-enter 350ms ease both;
      }
      @keyframes tile-enter {
        from { opacity: 0; transform: scale(0.92); }
        to   { opacity: 1; transform: scale(1); }
      }

      /* Disconnecting tile: dimmed appearance during grace period */
      .tile-disconnecting {
        opacity: 0.5;
        transition: opacity 300ms ease;
      }
      .tile-disconnecting .lk-participant-tile {
        filter: grayscale(0.6);
        transition: filter 300ms ease;
      }

      /* Hide LiveKit's built-in name/metadata overlay — we use our own ParticipantOverlay */
      .lk-participant-tile .lk-participant-name,
      .lk-participant-tile .lk-participant-metadata,
      .lk-participant-tile .lk-participant-metadata-item {
        display: none !important;
      }

      .lk-participant-tile[data-lk-local-participant="true"] video {
        transform: scaleX(1) !important;
      }
      
      .pip-container:hover .pip-button {
        opacity: 1 !important;
      }
      
      @container (max-width: 120px) {
        .participant-overlay {
          display: none !important;
        }
      }
      
      @container (max-height: 90px) {
        .participant-overlay {
          display: none !important;
        }
      }
      
      .lk-grid-layout {
        height: 100% !important;
        width: 100% !important;
        display: grid !important;
        gap: 0.5rem !important;
        padding: 0.5rem !important;
      }
      .lk-grid-layout:has(> :only-child) {
        grid-template-columns: 1fr !important;
        grid-template-rows: 1fr !important;
      }
      .lk-grid-layout:has(> :nth-child(2):nth-last-child(1)) {
        grid-template-columns: repeat(2, 1fr) !important;
        grid-template-rows: 1fr !important;
      }
      .lk-grid-layout:has(> :nth-child(3)),
      .lk-grid-layout:has(> :nth-child(4)) {
        grid-template-columns: repeat(2, 1fr) !important;
        grid-template-rows: repeat(2, 1fr) !important;
      }
      .lk-grid-layout:has(> :nth-child(5)),
      .lk-grid-layout:has(> :nth-child(6)) {
        grid-template-columns: repeat(3, 1fr) !important;
        grid-template-rows: repeat(2, 1fr) !important;
      }
      .lk-grid-layout:has(> :nth-child(7)),
      .lk-grid-layout:has(> :nth-child(8)),
      .lk-grid-layout:has(> :nth-child(9)) {
        grid-template-columns: repeat(3, 1fr) !important;
        grid-template-rows: repeat(3, 1fr) !important;
      }
      .lk-grid-layout:has(> :nth-child(10)) {
        grid-template-columns: repeat(4, 1fr) !important;
        grid-auto-rows: 1fr !important;
      }
      .lk-participant-tile {
        width: 100% !important;
        height: 100% !important;
        position: relative !important;
        background: #1f2937 !important;
        overflow: hidden !important;
        container-type: size;
        container-name: video-tile;
      }
      
      .lk-participant-tile video {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
      }
      
      .lk-toast-container,
      .lk-toast,
      [class*="lk-"][class*="toast"],
      [class*="lk-"][class*="notification"] {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
        position: absolute !important;
        left: -9999px !important;
      }
      
      .fixed.top-20:not(.screen-share-banner),
      div.fixed[class*="top-20"]:not(.screen-share-banner),
      div.bg-gradient-to-r.from-blue-600:not(.screen-share-banner),
      div[class*="from-blue-6"]:not(.screen-share-banner),
      .fixed.z-30:not(.screen-share-banner) {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
        position: absolute !important;
        left: -9999px !important;
      }
      
      .screen-share-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;
        position: relative;
      }
      .screen-share-banner {
        background: linear-gradient(90deg, #334155, #1e2937);
        color: #e2e8f0;
        padding: 10px 20px;
        display: flex !important;
        align-items: center;
        justify-content: center;
        gap: 10px;
        font-size: 15px;
        font-weight: 600;
        flex-shrink: 0;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        opacity: 1 !important;
        visibility: visible !important;
        position: relative !important;
        left: auto !important;
      }
      .screen-share-content {
        flex: 1;
        display: flex;
        width: 100%;
        position: relative;
        min-height: 0;
      }
      .screen-share-main {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #000;
        position: relative;
        min-height: 0;
        overflow: hidden;
      }
      
      .screen-share-main .lk-focus-layout {
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
      }
      .screen-share-main video {
        max-width: 100% !important;
        max-height: 100% !important;
        width: auto !important;
        height: auto !important;
        object-fit: contain !important;
      }
      
      .document-embed-container {
        display: flex;
        height: 100%;
        width: 100%;
        position: relative;
      }
      .document-embed-content {
        flex: 1;
        display: flex;
        width: 100%;
        position: relative;
      }
      .document-embed-main {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #000;
        position: relative;
      }
      .document-iframe {
        width: 100%;
        height: 100%;
        border: none;
        background: white;
      }
      
      .participants-sidebar {
        width: 280px;
        min-width: 280px;
        max-width: 320px;
        background: #1a1a1a;
        border-left: 1px solid #333;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 8px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex-shrink: 0;
      }
      .participants-sidebar .lk-grid-layout {
        display: flex !important;
        flex-direction: column !important;
        gap: 8px !important;
        grid-template-columns: none !important;
        grid-template-rows: none !important;
        grid-auto-rows: none !important;
        height: auto !important;
      }
      .participants-sidebar .lk-participant-tile {
        width: 100% !important;
        aspect-ratio: 16 / 9 !important;
        position: relative !important;
        border-radius: 8px;
        overflow: hidden;
        background: #1f2937;
        min-height: 0 !important;
        max-height: none !important;
        height: auto !important;
      }
      .participants-sidebar .lk-participant-tile video {
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
      }
    `;
    
    styleRef.current = style;
    document.head.appendChild(style);

    return () => {
      if (styleRef.current && styleRef.current.parentNode) {
        try {
          document.head.removeChild(styleRef.current);
        } catch (e) {
          // Silent cleanup
        }
        styleRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanup.cleanup();
      trackCacheRef.current.clear();
    };
  }, [cleanup]);

  if (visibleCameraTracks.length === 0 && visibleScreenShares.length === 0 && !embeddedDocument && !isWhiteboardActive) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-900">
        <div className="text-gray-400">{t('videoGrid.waitingForParticipants')}</div>
      </div>
    );
  }

  const shouldShowPresentationMode = visibleScreenShares.length > 0 || embeddedDocument || (isWhiteboardActive && !isWhiteboardDetached);

  // Phone layout: screen share + horizontal camera strip at top
  if (layoutMode === 'phone' && visibleScreenShares.length > 0) {
    const currentScreenShare = visibleScreenShares[activeScreenShareIndex] || visibleScreenShares[0];
    return (
      <div className="flex flex-col h-full w-full bg-gray-900">
        {visibleCameraTracks.length > 0 && (
          <PresentationSidebar
            tracks={visibleCameraTracks}
            participantVirtualRooms={participantVirtualRooms}
            virtualRooms={virtualRooms}
            updateTrigger={updateTrigger}
            teacherBroadcastRooms={teacherBroadcastRooms}
            teacherMonitorRooms={teacherMonitorRooms}
            teacherMonitorSilentRooms={teacherMonitorSilentRooms}
            isTeacher={isTeacher}
            gracePeriodParticipants={gracePeriodParticipants}
            horizontal={true}
          />
        )}
        <div className="flex-1 relative overflow-hidden">
          <ZoomableScreenShare
            key={`screenshare-${currentScreenShare?.publication?.trackSid}`}
            trackRef={currentScreenShare}
          />
        </div>
      </div>
    );
  }

  // Phone layout: whiteboard full-screen + horizontal camera strip at top
  if (layoutMode === 'phone' && isWhiteboardActive && !isWhiteboardDetached && whiteboardComponent) {
    return (
      <div className="flex flex-col h-full w-full bg-gray-900">
        {visibleCameraTracks.length > 0 && (
          <PresentationSidebar
            tracks={visibleCameraTracks}
            participantVirtualRooms={participantVirtualRooms}
            virtualRooms={virtualRooms}
            updateTrigger={updateTrigger}
            teacherBroadcastRooms={teacherBroadcastRooms}
            teacherMonitorRooms={teacherMonitorRooms}
            teacherMonitorSilentRooms={teacherMonitorSilentRooms}
            isTeacher={isTeacher}
            gracePeriodParticipants={gracePeriodParticipants}
            horizontal={true}
          />
        )}
        <div className="flex-1 overflow-hidden flex flex-col">
          {whiteboardComponent}
        </div>
      </div>
    );
  }

  if (shouldShowPresentationMode) {
    if (isWhiteboardActive && !isWhiteboardDetached && whiteboardComponent) {
      return (
        <div className="whiteboard-presentation-container" style={{ display: "flex", height: "100%", width: "100%" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {whiteboardComponent}
          </div>
          {visibleCameraTracks.length > 0 && (
            <PresentationSidebar
              tracks={visibleCameraTracks}
              participantVirtualRooms={participantVirtualRooms}
              virtualRooms={virtualRooms}
              updateTrigger={updateTrigger}
              teacherBroadcastRooms={teacherBroadcastRooms}
              teacherMonitorRooms={teacherMonitorRooms}
              teacherMonitorSilentRooms={teacherMonitorSilentRooms}
              isTeacher={isTeacher}
              gracePeriodParticipants={gracePeriodParticipants}
            />
          )}
        </div>
      );
    }
    if (embeddedDocument) {
      const isOwnDocument = documentSharer === 'self';
      
      return (
        <div className="document-embed-container">
          <div className="document-embed-content">
            <div className="document-embed-main relative">
              {isOwnDocument && docLoading && (
                <div className="absolute top-4 left-4 right-4 bg-blue-900/90 text-white p-3 rounded-lg z-20 text-sm">
                  <p className="mb-2">{t('videoGrid.document.tips.title')}</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>{t('videoGrid.document.tips.signIn')}</li>
                    <li>{t('videoGrid.document.tips.sharing')}</li>
                    <li>{t('videoGrid.document.tips.blankPage')}</li>
                  </ul>
                </div>
              )}
              
              {isOwnDocument && (
                <div className="absolute top-4 right-4 z-20 flex gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(embeddedDocument);
                      toast.success(t('videoGrid.document.linkCopied'));
                    }}
                    className="bg-gray-800/80 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-xs transition-colors"
                  >
                    {t('videoGrid.document.copyLink')}
                  </button>
                  <button
                    onClick={() => {
                      window.open('https://accounts.google.com/signin', '_blank', 'width=500,height=600');
                      toast.info(t('videoGrid.document.signInPrompt'));
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs transition-colors"
                  >
                    {t('videoGrid.document.signInGoogle')}
                  </button>
                </div>
              )}
              
              {docLoading && (
                <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
                  <div className="text-white">{t('videoGrid.document.loading')}</div>
                </div>
              )}
              
              <iframe 
                src={embeddedDocument}
                className="document-iframe"
                onLoad={handleIframeLoad}
                title={t('videoGrid.document.iframeTitle')}
                allow="camera; microphone; display-capture; clipboard-write"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-downloads allow-presentation"
              />
            </div>
            {visibleCameraTracks.length > 0 && (
              <PresentationSidebar
                tracks={visibleCameraTracks}
                participantVirtualRooms={participantVirtualRooms}
                virtualRooms={virtualRooms}
                updateTrigger={updateTrigger}
                teacherBroadcastRooms={teacherBroadcastRooms}
                teacherMonitorRooms={teacherMonitorRooms}
                teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                isTeacher={isTeacher}
                gracePeriodParticipants={gracePeriodParticipants}
              />
            )}
          </div>
        </div>
      );
    } else if (visibleScreenShares.length > 0) {
      const currentScreenShare = visibleScreenShares[activeScreenShareIndex] || visibleScreenShares[0];
      
      return (
        <div className="screen-share-container">
          <ScreenShareBanner
            screenShares={visibleScreenShares}
            activeIndex={activeScreenShareIndex}
            onSelectShare={setActiveScreenShareIndex}
            localParticipantIdentity={localParticipantIdentity}
          />
          <div className="screen-share-content">
            <div className="screen-share-main">
              <ZoomableScreenShare 
                key={`screenshare-${currentScreenShare?.publication?.trackSid}`}
                trackRef={currentScreenShare} 
              />
            </div>
            {visibleCameraTracks.length > 0 && (
              <PresentationSidebar
                tracks={visibleCameraTracks}
                participantVirtualRooms={participantVirtualRooms}
                virtualRooms={virtualRooms}
                updateTrigger={updateTrigger}
                teacherBroadcastRooms={teacherBroadcastRooms}
                teacherMonitorRooms={teacherMonitorRooms}
                teacherMonitorSilentRooms={teacherMonitorSilentRooms}
                isTeacher={isTeacher}
                gracePeriodParticipants={gracePeriodParticipants}
              />
            )}
          </div>
        </div>
      );
    }
  }

  return (
    <div className="relative h-full w-full">
      {isWhiteboardActive && isWhiteboardDetached && (
        <div className="absolute top-3 left-3 z-10 bg-indigo-600/90 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 shadow-lg backdrop-blur-sm">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          {t('whiteboard.detachedIndicator', 'Whiteboard on second screen')}
        </div>
      )}
      <DynamicGridLayout 
        tracks={visibleCameraTracks}
        participantVirtualRooms={participantVirtualRooms}
        virtualRooms={virtualRooms}
        updateTrigger={updateTrigger}
        teacherBroadcastRooms={teacherBroadcastRooms}
        teacherMonitorRooms={teacherMonitorRooms}
        teacherMonitorSilentRooms={teacherMonitorSilentRooms}
        isTeacher={isTeacher}
        gracePeriodParticipants={gracePeriodParticipants}
      >
        <TileComponent />
      </DynamicGridLayout>
    </div>
  );
};

export default memo(VideoGrid, (prevProps, nextProps) => {
  return false;
});